import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
} from '@mui/material';
import { apiClient } from '../../../lib/apiClient';
import { useUser } from '../../../context/UserContext';
import { debugLog } from '../../../utils/debug';

type InformationItem = {
  info_id: unknown;
  info_title?: unknown;
  info_message?: unknown;
  info_start_dt?: unknown;
  info_end_dt?: unknown;
  update_dt?: unknown;
  confirm_valid?: unknown;
};

const READ_INFO_IDS_CACHE_KEY = 'now-information-read-info-ids-v1';
const DEFAULT_TITLE = 'おしらせ';
const LEGACY_READ_MARK = '__legacy_read__';

const toIdString = (value: unknown): string => {
  if (value == null) return '';
  const text = String(value).trim();
  return text;
};

const toText = (value: unknown): string => {
  if (value == null) return '';
  return String(value);
};

type ReadInfoCache = Record<string, string>;

const readReadInfoCache = (): ReadInfoCache => {
  try {
    const raw = localStorage.getItem(READ_INFO_IDS_CACHE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      // 旧形式: ["1", "2", ...]
      const next: ReadInfoCache = {};
      parsed
        .map((v) => toIdString(v))
        .filter((v) => v.length > 0)
        .forEach((id) => {
          next[id] = LEGACY_READ_MARK;
        });
      return next;
    }
    if (parsed && typeof parsed === 'object') {
      const entries = Object.entries(parsed as Record<string, unknown>);
      const next: ReadInfoCache = {};
      entries.forEach(([k, v]) => {
        const id = toIdString(k);
        if (!id) return;
        next[id] = toText(v);
      });
      return next;
    }
    return {};
  } catch {
    return {};
  }
};

const appendReadInfoCache = (infoId: string, updateDt: string) => {
  if (!infoId) return;
  try {
    const cache = readReadInfoCache();
    cache[infoId] = updateDt;
    localStorage.setItem(READ_INFO_IDS_CACHE_KEY, JSON.stringify(cache));
  } catch {
    // localStorage が使えない環境では無視
  }
};

const pruneReadInfoCache = (activeInfoIds: Set<string>): { cache: ReadInfoCache; removedCount: number } => {
  try {
    const cache = readReadInfoCache();
    const pruned: ReadInfoCache = {};
    let removedCount = 0;
    Object.entries(cache).forEach(([infoId, updateDt]) => {
      if (!activeInfoIds.has(infoId)) {
        removedCount += 1;
        return;
      }
      pruned[infoId] = updateDt;
    });
    if (removedCount > 0) {
      localStorage.setItem(READ_INFO_IDS_CACHE_KEY, JSON.stringify(pruned));
    }
    return { cache: removedCount > 0 ? pruned : cache, removedCount };
  } catch {
    return { cache: {}, removedCount: 0 };
  }
};

const InformationNoticeDialogController: React.FC = () => {
  const authUser = useUser();
  const [queue, setQueue] = useState<InformationItem[]>([]);
  const [confirmChecked, setConfirmChecked] = useState(false);

  const current = queue.length > 0 ? queue[0] : null;
  const currentTitle = useMemo(() => {
    const title = toText(current?.info_title).trim();
    return title.length > 0 ? title : DEFAULT_TITLE;
  }, [current]);
  const currentMessage = useMemo(() => toText(current?.info_message), [current]);
  const requireConfirm = useMemo(
    () => String(current?.confirm_valid ?? '').trim().toUpperCase() === 'Y',
    [current],
  );

  useEffect(() => {
    if (!authUser) {
      debugLog('InformationNoticeDialog', 'authUser is empty. reset fetch state.');
      setQueue([]);
      setConfirmChecked(false);
      return;
    }

    debugLog('InformationNoticeDialog', 'authUser changed. fetch /get_now_information.');

    const abortController = new AbortController();
    (async () => {
      try {
        debugLog('InformationNoticeDialog', 'start GET /get_now_information');
        const res = await apiClient.get<InformationItem[]>('/get_now_information', {
          signal: abortController.signal,
        });
        const rows = Array.isArray(res.data) ? res.data : [];
        const activeInfoIds = new Set<string>(
          rows.map((row) => toIdString(row?.info_id)).filter((id) => id.length > 0),
        );
        const { cache: readCache, removedCount } = pruneReadInfoCache(activeInfoIds);
        const unread = rows.filter((row) => {
          const infoId = toIdString(row?.info_id);
          if (infoId.length === 0) return false;

          const latestUpdateDt = toText(row?.update_dt);
          const cachedUpdateDt = readCache[infoId];
          if (cachedUpdateDt == null) return true;
          if (cachedUpdateDt === LEGACY_READ_MARK) return cachedUpdateDt !== latestUpdateDt;
          return cachedUpdateDt !== latestUpdateDt;
        });
        debugLog('InformationNoticeDialog', 'success GET /get_now_information', {
          total: rows.length,
          willShow: unread.length,
          readCacheSize: Object.keys(readCache).length,
          removedFromCache: removedCount,
        });
        setQueue(unread);
        setConfirmChecked(false);
      } catch (e: any) {
        if (e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED') {
          debugLog('InformationNoticeDialog', 'canceled GET /get_now_information');
          return;
        }
        // 404 を含む取得失敗時は画面操作に影響を与えない（ダイアログを出さずに継続）
        const status = e?.response?.status;
        debugLog('InformationNoticeDialog', 'skip notice dialog: GET /get_now_information failed', {
          status: typeof status === 'number' ? status : null,
        });
        setQueue([]);
        setConfirmChecked(false);
      }
    })();

    return () => {
      debugLog('InformationNoticeDialog', 'cleanup -> abort GET /get_now_information');
      abortController.abort();
    };
  }, [authUser]);

  const handleCloseCurrent = () => {
    if (!current) return;
    if (requireConfirm && !confirmChecked) return;

    const infoId = toIdString(current.info_id);
    const updateDt = toText(current.update_dt);
    appendReadInfoCache(infoId, updateDt);
    setQueue((prev) => prev.slice(1));
    setConfirmChecked(false);
  };

  return (
    <Dialog
      open={!!current}
      onClose={(_event, reason) => {
        if (requireConfirm && !confirmChecked) return;
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          handleCloseCurrent();
          return;
        }
        handleCloseCurrent();
      }}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={requireConfirm && !confirmChecked}
      PaperProps={{
        sx: {
          borderRadius: 1,
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: '#202124',
          color: '#ffffff',
          fontSize: '0.9rem',
          fontWeight: 700,
          lineHeight: 1.3,
          py: 1,
        }}
      >
        {currentTitle}
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', fontSize: '1rem' }}>
          {currentMessage}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <div style={{ marginLeft: 8 }}>
          {requireConfirm && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={confirmChecked}
                  onChange={(e) => setConfirmChecked(e.target.checked)}
                />
              }
              label="確認しました"
            />
          )}
        </div>
        <Button onClick={handleCloseCurrent} variant="contained" disabled={requireConfirm && !confirmChecked}>
          閉じる
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InformationNoticeDialogController;
